import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';
import SvgEditor from './components/SvgEditor';
import Toolbar from './components/Toolbar';
import CodePanel from './components/CodePanel';
import FileUpload from './components/FileUpload';
import HistoryControl from './components/HistoryControl';

const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: #f5f5f5;
  position: relative;
`;

const DropOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  color: white;
  font-size: 24px;
  pointer-events: none;
  opacity: ${props => props.visible ? 1 : 0};
  transition: opacity 0.3s ease;
`;

const DropMessage = styled.div`
  padding: 30px;
  border: 3px dashed white;
  border-radius: 10px;
  background-color: rgba(0, 0, 0, 0.5);
  text-align: center;
`;

const Header = styled.header`
  background-color: #333;
  color: white;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  height: 50px;
`;

const AppTitle = styled.h1`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 500;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
`;

const MainContent = styled.main`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

const EditorSection = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

const PreviewSection = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  position: relative;
`;

function App() {
  const [svgCode, setSvgCode] = useState('<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400" viewBox="0 0 124 124" fill="none"><rect width="124" height="124" rx="24" fill="#F97316"/></svg>');
  const [isDragging, setIsDragging] = useState(false);
  const appRef = useRef(null);
  
  // History state
  const [history, setHistory] = useState([svgCode]);
  const [historyIndex, setHistoryIndex] = useState(0);
  
  // Update history when SVG code changes
  const handleSvgCodeChange = useCallback((newCode) => {
    setSvgCode(newCode);
    
    // Add to history if it's a new state
    if (newCode !== history[historyIndex]) {
      const newHistory = history.slice(0, historyIndex + 1);
      newHistory.push(newCode);
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    }
  }, [history, historyIndex]);
  
  const handleUndo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setSvgCode(history[historyIndex - 1]);
    }
  };
  
  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setSvgCode(history[historyIndex + 1]);
    }
  };
  
  // Process SVG content with useCallback to memoize
  const processSvgContent = useCallback((content) => {
    try {
      const parser = new DOMParser();
      const svgDoc = parser.parseFromString(content, 'image/svg+xml');
      const svgElement = svgDoc.querySelector('svg');
      
      if (svgElement) {
        // Ensure the SVG has width and height attributes
        if (!svgElement.hasAttribute('width') || !svgElement.hasAttribute('height')) {
          // Get viewBox values or set defaults
          let viewBox = svgElement.getAttribute('viewBox');
          let width = 400;
          let height = 400;
          
          if (viewBox) {
            const viewBoxValues = viewBox.split(' ').map(Number);
            if (viewBoxValues.length === 4) {
              width = viewBoxValues[2];
              height = viewBoxValues[3];
            }
          } else {
            // If no viewBox, create one
            viewBox = `0 0 ${width} ${height}`;
            svgElement.setAttribute('viewBox', viewBox);
          }
          
          // Set width and height attributes
          svgElement.setAttribute('width', width);
          svgElement.setAttribute('height', height);
        }
        
        // Ensure the SVG has a viewBox if it has width and height
        if (!svgElement.hasAttribute('viewBox') && 
            svgElement.hasAttribute('width') && 
            svgElement.hasAttribute('height')) {
          const width = svgElement.getAttribute('width');
          const height = svgElement.getAttribute('height');
          svgElement.setAttribute('viewBox', `0 0 ${width} ${height}`);
        }
        
        // Convert the SVG back to a string
        const serializer = new XMLSerializer();
        const processedSvg = serializer.serializeToString(svgDoc);
        
        // Update the SVG code
        handleSvgCodeChange(processedSvg);
      } else {
        alert('Invalid SVG structure.');
      }
    } catch (error) {
      console.error('Error processing SVG:', error);
      // If processing fails, use the original content
      handleSvgCodeChange(content);
    }
  }, [handleSvgCodeChange]);
  
  // Global drag and drop handlers
  useEffect(() => {
    const handleDragOver = (e) => {
      e.preventDefault();
      setIsDragging(true);
    };
    
    const handleDragEnter = (e) => {
      e.preventDefault();
      setIsDragging(true);
    };
    
    const handleDragLeave = (e) => {
      e.preventDefault();
      // Only set isDragging to false if we're leaving the app container
      if (e.relatedTarget === null || !appRef.current.contains(e.relatedTarget)) {
        setIsDragging(false);
      }
    };
    
    const handleDrop = (e) => {
      e.preventDefault();
      setIsDragging(false);
      
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        const file = files[0];
        
        // Check if it's an SVG file
        if (file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg')) {
          const reader = new FileReader();
          reader.onload = (event) => {
            const content = event.target.result;
            if (content.includes('<svg')) {
              processSvgContent(content);
            } else {
              alert('The dropped file does not contain valid SVG content.');
            }
          };
          reader.readAsText(file);
        } else {
          alert('Please drop an SVG file.');
        }
      }
    };
    
    document.addEventListener('dragover', handleDragOver);
    document.addEventListener('dragenter', handleDragEnter);
    document.addEventListener('dragleave', handleDragLeave);
    document.addEventListener('drop', handleDrop);
    
    return () => {
      document.removeEventListener('dragover', handleDragOver);
      document.removeEventListener('dragenter', handleDragEnter);
      document.removeEventListener('dragleave', handleDragLeave);
      document.removeEventListener('drop', handleDrop);
    };
  }, [processSvgContent]);
  
  return (
    <>
      <Helmet>
        <title>SVG Viewer & Editor | Online SVG Editing Tool</title>
        <meta name="description" content="Free online SVG viewer and editor. Edit, optimize, and export SVG files easily. Manipulate paths, colors, and metadata with this powerful browser-based tool." />
      </Helmet>
      
      <AppContainer ref={appRef}>
        {isDragging && (
          <DropOverlay visible={isDragging}>
            <DropMessage>
              Drop your SVG file here to load it
            </DropMessage>
          </DropOverlay>
        )}
        
        <Header>
          <AppTitle>SVG Viewer & Editor</AppTitle>
          <ButtonGroup>
            <FileUpload setSvgCode={handleSvgCodeChange} />
          </ButtonGroup>
        </Header>
        
        <MainContent role="main">
          <EditorSection>
            <Toolbar svgCode={svgCode} setSvgCode={handleSvgCodeChange} />
            <CodePanel svgCode={svgCode} setSvgCode={handleSvgCodeChange} />
          </EditorSection>
          
          <PreviewSection>
            <SvgEditor svgCode={svgCode} setSvgCode={handleSvgCodeChange} />
            <HistoryControl 
              canUndo={historyIndex > 0}
              canRedo={historyIndex < history.length - 1}
              onUndo={handleUndo}
              onRedo={handleRedo}
            />
          </PreviewSection>
        </MainContent>
        
        {/* <footer style={{ padding: '10px', textAlign: 'center', fontSize: '12px', color: '#666', borderTop: '1px solid #ddd' }}>
          <p>© {new Date().getFullYear()} SVG Viewer & Editor. All rights reserved.</p>
          <p>
            <a href="/privacy" style={{ color: '#666', marginRight: '10px' }}>Privacy Policy</a>
            <a href="/terms" style={{ color: '#666' }}>Terms of Service</a>
          </p>
        </footer> */}
      </AppContainer>
    </>
  );
}

export default App; 